import { API_BASE_URL } from '../config';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
}

export interface UserResponse {
  id: number;
  email: string;
  name: string;
  phone?: string;
  job_title?: string;
  company?: string;
  department?: string;
  avatar_url?: string;
  role: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  last_login?: string;
  scopes?: string[];
}

class ApiService {
  private isRefreshing = false;
  private failedQueue: any[] = [];

  private processQueue(error: any, token: string | null = null) {
    this.failedQueue.forEach(prom => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    this.failedQueue = [];
  }
  private getAuthToken(): string | null {
    return localStorage.getItem('access_token');
  }

  private normalizeEndpoint(endpoint: string): string {
    // Endpoints that require trailing slash (list endpoints that use @router.get("/"))
    const endpointsRequiringTrailingSlash = [
      '/api/categories',
      '/api/companies',
      '/api/pages',
      '/api/users',
      '/api/group-scopes',
      '/api/logs',
      '/api/agreement-types',
      '/api/group-pages',
      '/api/scopes',
      '/api/statuses',
      '/api/templates',
      '/api/tasks',
      '/api/groups',
      '/api/user-groups',
      '/api/task-assignments',
      '/api/task-ai-compare',
    ];

    // Split endpoint into path and query string
    const [path, queryString] = endpoint.split('?');

    // Check if this endpoint requires trailing slash (exact match only, not sub-paths)
    const needsTrailingSlash = endpointsRequiringTrailingSlash.includes(path);

    if (needsTrailingSlash) {
      // Add trailing slash to path if not present
      const normalizedPath = path.endsWith('/') ? path : path + '/';
      // Reconstruct endpoint with query string if exists
      return queryString ? `${normalizedPath}?${queryString}` : normalizedPath;
    }

    return endpoint;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = this.getAuthToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> || {}),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Normalize endpoint to handle trailing slash requirements
    const normalizedEndpoint = this.normalizeEndpoint(endpoint);

    try {
      const response = await fetch(`${API_BASE_URL}${normalizedEndpoint}`, {
        ...options,
        headers,
      });

      // Handle 401 Unauthorized
      if (response.status === 401) {
        // Don't attempt to refresh if we're already trying to login or refresh
        if (endpoint.includes('/auth/login') || endpoint.includes('/auth/refresh')) {
          // If refresh fails, clear everything and throw
          if (endpoint.includes('/auth/refresh')) {
            this.logout();
            
            let errMsg = 'Unauthorized';
            try {
              const errData = await response.json();
              errMsg = errData.detail || errMsg;
            } catch (e) {
              // ignore
            }
            
            // Only redirect if we are not already on the login page
            if (!window.location.href.includes('/login')) {
                // You can optionally pass the error message as a query param if your login page supports it
                window.location.href = `/login?error=${encodeURIComponent(errMsg)}`;
            }
          }
          throw new Error('Unauthorized');
        }

        if (this.isRefreshing) {
          return new Promise((resolve, reject) => {
            this.failedQueue.push({ resolve, reject });
          }).then(token => {
            // Retry with new token
            if (options.headers) {
              (options.headers as any)['Authorization'] = `Bearer ${token}`;
            }
            return this.request<T>(endpoint, options);
          }).catch(err => {
            return Promise.reject(err);
          });
        }

        this.isRefreshing = true;

        try {
          const { access_token } = await this.refreshToken();
          this.processQueue(null, access_token);

          // Retry original request
          if (options.headers) {
            (options.headers as any)['Authorization'] = `Bearer ${access_token}`;
          }
          // Recursive call but since we have new token it shouldn't loop unless 401 happens again immediately
          return this.request<T>(endpoint, options);
        } catch (err) {
          this.processQueue(err, null);
          this.logout();
          window.location.href = '/login';
          throw err;
        } finally {
          this.isRefreshing = false;
        }
      }

      if (!response.ok) {
        let errorMessage = response.statusText;
        try {
          const error = await response.json();
          errorMessage = error.detail || error.message || errorMessage;
        } catch {
          // If response is not JSON, use statusText
        }
        throw new Error(errorMessage || `HTTP error! status: ${response.status}`);
      }

      // Handle empty responses
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const text = await response.text();
        return text ? JSON.parse(text) : ({} as T);
      }

      return response.json();
    } catch (error: any) {
      // Handle network errors
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Unable to connect to server. Please check if the backend is running.');
      }
      throw error;
    }
  }

  // Auth endpoints
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await this.request<LoginResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });

    // Store tokens
    localStorage.setItem('access_token', response.access_token);
    localStorage.setItem('refresh_token', response.refresh_token);

    return response;
  }

  async register(userData: any): Promise<any> {
    return this.request('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async refreshToken(): Promise<LoginResponse> {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    // Direct fetch to avoid circular dependency in request()
    // We can't use this.request() because it might trigger another 401 intercept loop
    const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) {
      throw new Error('Failed to refresh token');
    }

    const data: LoginResponse = await response.json();

    // Update tokens
    localStorage.setItem('access_token', data.access_token);
    localStorage.setItem('refresh_token', data.refresh_token);

    return data;
  }

  // User endpoints
  async getCurrentUser(): Promise<UserResponse> {
    return this.request<UserResponse>('/api/users/me');
  }

  async getCurrentUserScopes(): Promise<string[]> {
    return this.request<string[]>('/api/users/me/scopes');
  }

  async getUsers(): Promise<UserResponse[]> {
    return this.request<UserResponse[]>('/api/users/');
  }

  async getUser(userId: number): Promise<UserResponse> {
    return this.request<UserResponse>(`/api/users/${userId}`);
  }

  async updateUser(userId: number, userData: any): Promise<UserResponse> {
    return this.request<UserResponse>(`/api/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  async deleteUser(userId: number): Promise<void> {
    return this.request<void>(`/api/users/${userId}`, {
      method: 'DELETE',
    });
  }

  async updateCurrentUser(userData: any): Promise<UserResponse> {
    // First get current user to get user ID
    const currentUser = await this.getCurrentUser();
    return this.updateUser(currentUser.id, userData);
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const currentUser = await this.getCurrentUser();
    // Update password through user update endpoint
    await this.updateUser(currentUser.id, {
      password: newPassword
    });
  }

  // Group endpoints
  async getGroups(): Promise<any[]> {
    return this.request<any[]>('/api/groups');
  }

  async getGroup(groupId: number): Promise<any> {
    return this.request<any>(`/api/groups/${groupId}`);
  }

  async createGroup(groupData: any): Promise<any> {
    return this.request<any>('/api/groups', {
      method: 'POST',
      body: JSON.stringify(groupData),
    });
  }

  async updateGroup(groupId: number, groupData: any): Promise<any> {
    return this.request<any>(`/api/groups/${groupId}`, {
      method: 'PUT',
      body: JSON.stringify(groupData),
    });
  }

  async deleteGroup(groupId: number): Promise<void> {
    return this.request<void>(`/api/groups/${groupId}`, {
      method: 'DELETE',
    });
  }

  // Page endpoints
  async getPages(): Promise<any[]> {
    return this.request<any[]>('/api/pages');
  }

  async getPage(pageId: number): Promise<any> {
    return this.request<any>(`/api/pages/${pageId}`);
  }

  async createPage(pageData: any): Promise<any> {
    return this.request<any>('/api/pages', {
      method: 'POST',
      body: JSON.stringify(pageData),
    });
  }

  async updatePage(pageId: number, pageData: any): Promise<any> {
    return this.request<any>(`/api/pages/${pageId}`, {
      method: 'PUT',
      body: JSON.stringify(pageData),
    });
  }

  async deletePage(pageId: number): Promise<void> {
    return this.request<void>(`/api/pages/${pageId}`, {
      method: 'DELETE',
    });
  }

  // User-Group endpoints
  async getUserGroups(userId?: number, groupId?: number): Promise<any[]> {
    const params = new URLSearchParams();
    if (userId) params.append('user_id', userId.toString());
    if (groupId) params.append('group_id', groupId.toString());
    const query = params.toString();
    return this.request<any[]>(`/api/user-groups${query ? '?' + query : ''}`);
  }

  async assignUserToGroup(userId: number, groupId: number): Promise<any> {
    return this.request<any>('/api/user-groups', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, group_id: groupId }),
    });
  }

  async removeUserFromGroup(userGroupId: number): Promise<void> {
    return this.request<void>(`/api/user-groups/${userGroupId}`, {
      method: 'DELETE',
    });
  }

  // Scope endpoints
  async getScopes(pageId?: number): Promise<any[]> {
    const params = new URLSearchParams();
    if (pageId) params.append('page_id', pageId.toString());
    const query = params.toString();
    return this.request<any[]>(`/api/scopes${query ? '?' + query : ''}`);
  }

  async getScope(scopeId: number): Promise<any> {
    return this.request<any>(`/api/scopes/${scopeId}`);
  }

  async createScope(scopeData: any): Promise<any> {
    return this.request<any>('/api/scopes', {
      method: 'POST',
      body: JSON.stringify(scopeData),
    });
  }

  async updateScope(scopeId: number, scopeData: any): Promise<any> {
    return this.request<any>(`/api/scopes/${scopeId}`, {
      method: 'PUT',
      body: JSON.stringify(scopeData),
    });
  }

  async deleteScope(scopeId: number): Promise<void> {
    return this.request<void>(`/api/scopes/${scopeId}`, {
      method: 'DELETE',
    });
  }

  // Group-Scope endpoints
  async getGroupScopes(groupId?: number, scopeId?: number): Promise<any[]> {
    const params = new URLSearchParams();
    if (groupId) params.append('group_id', groupId.toString());
    if (scopeId) params.append('scope_id', scopeId.toString());
    const query = params.toString();
    return this.request<any[]>(`/api/group-scopes${query ? '?' + query : ''}`);
  }

  async assignScopeToGroup(groupId: number, scopeId: number, permissions: { can_access: boolean }): Promise<any> {
    return this.request<any>('/api/group-scopes', {
      method: 'POST',
      body: JSON.stringify({
        group_id: groupId,
        scope_id: scopeId,
        can_access: permissions.can_access
      }),
    });
  }

  async updateGroupScopePermissions(groupScopeId: number, permissions: { can_access: boolean }): Promise<any> {
    return this.request<any>(`/api/group-scopes/${groupScopeId}`, {
      method: 'PUT',
      body: JSON.stringify({
        can_access: permissions.can_access
      }),
    });
  }

  async removeScopeFromGroup(groupScopeId: number): Promise<void> {
    return this.request<void>(`/api/group-scopes/${groupScopeId}`, {
      method: 'DELETE',
    });
  }

  // Logs endpoints
  async getLogs(params?: {
    skip?: number;
    limit?: number;
    user_id?: number;
    action?: string;
    resource_type?: string;
  }): Promise<any[]> {
    const queryParams = new URLSearchParams();
    if (params?.skip !== undefined) queryParams.append('skip', params.skip.toString());
    if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString());
    if (params?.user_id !== undefined) queryParams.append('user_id', params.user_id.toString());
    if (params?.action) queryParams.append('action', params.action);
    if (params?.resource_type) queryParams.append('resource_type', params.resource_type);
    const query = queryParams.toString();
    return this.request<any[]>(`/api/logs${query ? '?' + query : ''}`);
  }

  async getLog(logId: number): Promise<any> {
    return this.request<any>(`/api/logs/${logId}`);
  }

  // Company endpoints
  async getCompanies(): Promise<any[]> {
    return this.request<any[]>('/api/companies');
  }

  async getCompany(companyId: number): Promise<any> {
    return this.request<any>(`/api/companies/${companyId}`);
  }

  async createCompany(data: { name: string; code: string; description?: string; logo_url?: string; is_active?: boolean }): Promise<any> {
    return this.request<any>('/api/companies', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCompany(companyId: number, data: { name?: string; code?: string; description?: string; logo_url?: string; is_active?: boolean }): Promise<any> {
    return this.request<any>(`/api/companies/${companyId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCompany(companyId: number): Promise<void> {
    return this.request<void>(`/api/companies/${companyId}`, {
      method: 'DELETE',
    });
  }

  // Category endpoints
  async getCategories(): Promise<any[]> {
    return this.request<any[]>('/api/categories');
  }

  async getCategory(categoryId: number): Promise<any> {
    return this.request<any>(`/api/categories/${categoryId}`);
  }

  async createCategory(data: { name: string; description?: string; icon_class?: string }): Promise<any> {
    return this.request<any>('/api/categories', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCategory(categoryId: number, data: { name?: string; description?: string; icon_class?: string }): Promise<any> {
    return this.request<any>(`/api/categories/${categoryId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCategory(categoryId: number): Promise<void> {
    return this.request<void>(`/api/categories/${categoryId}`, {
      method: 'DELETE',
    });
  }

  // Agreement Type endpoints
  async getAgreementTypes(): Promise<any[]> {
    return this.request<any[]>('/api/agreement-types');
  }

  async getAgreementType(agreementTypeId: number): Promise<any> {
    return this.request<any>(`/api/agreement-types/${agreementTypeId}`);
  }

  async createAgreementType(data: { name: string; description?: string; icon_class?: string }): Promise<any> {
    return this.request<any>('/api/agreement-types', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAgreementType(agreementTypeId: number, data: { name?: string; description?: string; icon_class?: string }): Promise<any> {
    return this.request<any>(`/api/agreement-types/${agreementTypeId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteAgreementType(agreementTypeId: number): Promise<void> {
    return this.request<void>(`/api/agreement-types/${agreementTypeId}`, {
      method: 'DELETE',
    });
  }

  // Status endpoints
  async getStatuses(): Promise<any[]> {
    return this.request<any[]>('/api/statuses');
  }

  async getStatus(statusId: number): Promise<any> {
    return this.request<any>(`/api/statuses/${statusId}`);
  }

  async createStatus(data: { name: string; description?: string; color_class?: string; sla_days?: number | null; sort_order?: number | null }): Promise<any> {
    return this.request<any>('/api/statuses', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateStatus(statusId: number, data: { name?: string; description?: string; color_class?: string; sla_days?: number | null; sort_order?: number | null }): Promise<any> {
    return this.request<any>(`/api/statuses/${statusId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Summary (workflow dashboard)
  async getSummary(params?: { status_id?: number | null; search?: string }): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params?.status_id !== undefined && params?.status_id !== null) queryParams.append('status_id', String(params.status_id));
    if (params?.search && params.search.trim() !== '') queryParams.append('search', params.search.trim());
    const query = queryParams.toString();
    return this.request<any>(`/api/summary${query ? '?' + query : ''}`);
  }

  async getSummaryTask(taskId: number): Promise<any> {
    return this.request<any>(`/api/summary/tasks/${taskId}`);
  }

  async deleteStatus(statusId: number): Promise<void> {
    return this.request<void>(`/api/statuses/${statusId}`, {
      method: 'DELETE',
    });
  }

  // Template endpoints
  async getTemplates(params?: {
    skip?: number;
    limit?: number;
    category_id?: number;
    agreement_type_id?: number;
    is_active?: number;
    search?: string;
    template_type?: string;
  }): Promise<any[]> {
    const queryParams = new URLSearchParams();
    if (params?.skip !== undefined) queryParams.append('skip', params.skip.toString());
    if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString());
    if (params?.category_id !== undefined) queryParams.append('category_id', params.category_id.toString());
    if (params?.agreement_type_id !== undefined) queryParams.append('agreement_type_id', params.agreement_type_id.toString());
    if (params?.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());
    if (params?.search !== undefined && params.search.trim() !== '') queryParams.append('search', params.search.trim());
    if (params?.template_type !== undefined && params.template_type.trim() !== '') queryParams.append('template_type', params.template_type.trim());
    const query = queryParams.toString();
    return this.request<any[]>(`/api/templates${query ? '?' + query : ''}`);
  }

  async getTemplateCount(params?: {
    category_id?: number;
    agreement_type_id?: number;
    is_active?: number;
  }): Promise<{ count: number }> {
    const queryParams = new URLSearchParams();
    if (params?.category_id !== undefined) queryParams.append('category_id', params.category_id.toString());
    if (params?.agreement_type_id !== undefined) queryParams.append('agreement_type_id', params.agreement_type_id.toString());
    if (params?.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());
    const query = queryParams.toString();
    return this.request<{ count: number }>(`/api/templates/count${query ? '?' + query : ''}`);
  }

  async getTemplateCountByCategory(): Promise<Record<string, number>> {
    return this.request<Record<string, number>>('/api/templates/count-by-category');
  }

  async getTemplate(templateId: number): Promise<any> {
    return this.request<any>(`/api/templates/${templateId}`);
  }

  async checkDuplicateTemplate(categoryId: number, agreementTypeId: number, templateType?: string): Promise<any | null> {
    let url = `/api/templates/check-duplicate?category_id=${categoryId}&agreement_type_id=${agreementTypeId}`;
    if (templateType) {
      url += `&template_type=${templateType}`;
    }
    return this.request<any | null>(url);
  }

  async saveDocument(file: File, title?: string): Promise<any> {
    const token = this.getAuthToken();
    const formData = new FormData();
    formData.append('file', file);
    if (title) {
      formData.append('title', title);
    }

    const response = await fetch(`${API_BASE_URL}/api/documents/save`, {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` })
      },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to save document');
    }

    return response.json();
  }

  async createTemplate(formData: FormData): Promise<any> {
    const token = this.getAuthToken();

    // Don't set Content-Type header - browser will automatically set it with boundary for FormData
    // Setting it manually will break the multipart/form-data encoding

    try {
      const response = await fetch(`${API_BASE_URL}/api/templates/`, {
        method: 'POST',
        headers: {
          // Only set Authorization header, let browser handle Content-Type
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: formData,
      });

      if (!response.ok) {
        let errorMessage = response.statusText;
        try {
          const error = await response.json();
          // Handle FastAPI validation errors
          if (error.detail) {
            if (Array.isArray(error.detail)) {
              const errors = error.detail.map((e: any) => `${e.loc?.join('.')}: ${e.msg}`).join(', ');
              errorMessage = errors;
            } else {
              errorMessage = typeof error.detail === 'string' ? error.detail : JSON.stringify(error.detail);
            }
          } else {
            errorMessage = error.message || errorMessage;
          }
        } catch {
          // If response is not JSON, use statusText
        }
        throw new Error(errorMessage || `HTTP error! status: ${response.status}`);
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const text = await response.text();
        return text ? JSON.parse(text) : {};
      }

      return response.json();
    } catch (error: any) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Unable to connect to server. Please check if the backend is running.');
      }
      throw error;
    }
  }

  async updateTemplate(templateId: number, templateData: any): Promise<any> {
    return this.request<any>(`/api/templates/${templateId}`, {
      method: 'PUT',
      body: JSON.stringify(templateData),
    });
  }

  async deleteTemplate(templateId: number): Promise<void> {
    return this.request<void>(`/api/templates/${templateId}`, {
      method: 'DELETE',
    });
  }

  logout(): void {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
  }

  // Task endpoints
  async createTask(taskData: any): Promise<any> {
    return this.request<any>('/api/tasks', {
      method: 'POST',
      body: JSON.stringify(taskData),
    });
  }

  async getTasks(params?: {
    skip?: number;
    limit?: number;
    category_id?: number;
    agreement_type_id?: number;
    status_id?: number;
    created_by?: number;
    is_active?: number;
    filter_type?: 'all' | 'issued_by_me' | 'assigned_to_me' | 'my_team' | string;
    search?: string;
    template_type?: string;
  }): Promise<any[]> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (key === 'search' && typeof value === 'string' && value.trim() !== '') {
            queryParams.append(key, value.trim());
          } else if (key !== 'search') {
            queryParams.append(key, value.toString());
          }
        }
      });
    }
    const query = queryParams.toString();
    return this.request<any[]>(`/api/tasks${query ? '?' + query : ''}`);
  }

  async getTask(taskId: number): Promise<any> {
    return this.request<any>(`/api/tasks/${taskId}`);
  }

  async updateTask(taskId: number, taskData: any): Promise<any> {
    return this.request<any>(`/api/tasks/${taskId}`, {
      method: 'PUT',
      body: JSON.stringify(taskData),
    });
  }

  async deleteTask(taskId: number): Promise<void> {
    return this.request<void>(`/api/tasks/${taskId}`, {
      method: 'DELETE',
    });
  }

  // Task Comments
  async getTaskComments(taskId: number, params?: { skip?: number; limit?: number }): Promise<any[]> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    const query = queryParams.toString();
    return this.request<any[]>(`/api/tasks/${taskId}/comments/${query ? '?' + query : ''}`);
  }

  async createTaskComment(taskId: number, content: string, mentions?: Array<{ user_id?: number; group_id?: number; mention_type: string }>): Promise<any> {
    return this.request<any>(`/api/tasks/${taskId}/comments/`, {
      method: 'POST',
      body: JSON.stringify({ content, mentions: mentions || [] }),
    });
  }

  async updateTaskComment(taskId: number, commentId: number, content: string, mentions?: Array<{ user_id?: number; group_id?: number; mention_type: string }>): Promise<any> {
    return this.request<any>(`/api/tasks/${taskId}/comments/${commentId}`, {
      method: 'PUT',
      body: JSON.stringify({ content, mentions: mentions || [] }),
    });
  }

  async deleteTaskComment(taskId: number, commentId: number): Promise<void> {
    return this.request<void>(`/api/tasks/${taskId}/comments/${commentId}`, {
      method: 'DELETE',
    });
  }

  // Review Comments
  async getReviewComments(taskId: number, params?: { skip?: number; limit?: number; is_resolved?: number }): Promise<any[]> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    const query = queryParams.toString();
    return this.request<any[]>(`/api/tasks/${taskId}/review-comments/${query ? '?' + query : ''}`);
  }

  async createReviewComment(
    taskId: number,
    content: string,
    selectedText?: string,
    startOffset?: number,
    endOffset?: number,
    mentions?: Array<{ user_id?: number; group_id?: number; mention_type: string }>,
    parentCommentId?: number
  ): Promise<any> {
    return this.request<any>(`/api/tasks/${taskId}/review-comments/`, {
      method: 'POST',
      body: JSON.stringify({
        content,
        selected_text: selectedText,
        start_offset: startOffset,
        end_offset: endOffset,
        mentions: mentions || [],
        parent_comment_id: parentCommentId
      }),
    });
  }

  async flagReviewCommentAsDone(taskId: number, commentId: number): Promise<any> {
    return this.request<any>(`/api/tasks/${taskId}/review-comments/${commentId}/flag-done`, {
      method: 'POST',
    });
  }

  async unflagReviewCommentAsDone(taskId: number, commentId: number): Promise<any> {
    return this.request<any>(`/api/tasks/${taskId}/review-comments/${commentId}/unflag-done`, {
      method: 'POST',
    });
  }

  async replyToReviewComment(
    taskId: number,
    commentId: number,
    content: string,
    mentions?: Array<{ user_id?: number; group_id?: number; mention_type: string }>
  ): Promise<any> {
    return this.request<any>(`/api/tasks/${taskId}/review-comments/${commentId}/reply`, {
      method: 'POST',
      body: JSON.stringify({
        content,
        mentions: mentions || []
      }),
    });
  }

  async updateReviewComment(
    taskId: number,
    commentId: number,
    data: { content?: string; is_resolved?: number }
  ): Promise<any> {
    return this.request<any>(`/api/tasks/${taskId}/review-comments/${commentId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteReviewComment(taskId: number, commentId: number): Promise<void> {
    return this.request<void>(`/api/tasks/${taskId}/review-comments/${commentId}`, {
      method: 'DELETE',
    });
  }

  // Task Logs
  async getTaskLogs(taskId: number, params?: { skip?: number; limit?: number }): Promise<any[]> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    const query = queryParams.toString();
    return this.request<any[]>(`/api/logs/task/${taskId}${query ? '?' + query : ''}`);
  }

  // Task Assignments
  async getTaskAssignments(taskId: number): Promise<any[]> {
    return this.request<any[]>(`/api/task-assignments/task/${taskId}`);
  }

  async getMyAssignedTasks(params?: { is_active?: number; skip?: number; limit?: number }): Promise<any[]> {
    const queryParams = new URLSearchParams();
    if (params?.is_active !== undefined) queryParams.append('is_active', String(params.is_active));
    if (params?.skip !== undefined) queryParams.append('skip', String(params.skip));
    if (params?.limit !== undefined) queryParams.append('limit', String(params.limit));

    const queryString = queryParams.toString();
    const url = `/api/task-assignments/user/me/tasks${queryString ? `?${queryString}` : ''}`;
    return this.request<any[]>(url);
  }

  async createTaskAssignment(data: { task_id: number; user_id: number; comment?: string }): Promise<any> {
    return this.request<any>('/api/task-assignments', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTaskAssignment(assignmentId: number, data: { comment?: string }): Promise<any> {
    return this.request<any>(`/api/task-assignments/${assignmentId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteTaskAssignment(assignmentId: number): Promise<void> {
    return this.request<void>(`/api/task-assignments/${assignmentId}`, {
      method: 'DELETE',
    });
  }

  // Document Versions
  async getTaskVersions(taskId: number): Promise<any[]> {
    return this.request<any[]>(`/api/tasks/${taskId}/versions`);
  }

  async uploadTaskVersion(taskId: number, file: File, note?: string) {
    const formData = new FormData();
    formData.append('file', file);
    if (note) {
      formData.append('note', note);
    }

    const token = localStorage.getItem('access_token');
    const response = await fetch(`${API_BASE_URL}/api/tasks/${taskId}/versions`, {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
        // No Content-Type header so browser sets it with boundary
      },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to upload version');
    }

    return response.json();
  }

  async createTaskVersionSnapshot(taskId: number) {
    return this.request<any>(`/api/tasks/${taskId}/versions/snapshot`, {
      method: 'POST',
    });
  }

  async setTaskVersionCurrent(taskId: number, versionId: number): Promise<any> {
    return this.request<any>(`/api/tasks/${taskId}/versions/${versionId}/set-current`, {
      method: 'POST',
    });
  }

  async downloadTaskVersionPdf(taskId: number, versionId: number): Promise<Blob> {
    const token = localStorage.getItem('access_token');
    const response = await fetch(`${API_BASE_URL}/api/tasks/versions/${versionId}/pdf`, {
      method: 'GET',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
      }
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to download PDF');
    }

    return response.blob();
  }



  async getTaskVersionBlob(taskId: number, versionId: number): Promise<Blob> {
    const token = localStorage.getItem('access_token');
    // Reuse the current-document endpoint which supports version_id param
    // Endpoint: /api/tasks/{id}/current-document?version_id={version_id}
    const response = await fetch(`${API_BASE_URL}/api/tasks/${taskId}/current-document?version_id=${versionId}`, {
      method: 'GET',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch document version file');
    }

    return response.blob();
  }

  // Attachments
  async getTaskAttachments(taskId: number): Promise<any[]> {
    return this.request<any[]>(`/api/tasks/${taskId}/attachments`);
  }

  async createAttachment(taskId: number, formData: FormData): Promise<any> {
    const token = this.getAuthToken();
    const response = await fetch(`${API_BASE_URL}/api/tasks/${taskId}/attachments`, {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` })
      },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create attachment');
    }

    return response.json();
  }

  async updateAttachment(attachmentId: number, formData: FormData): Promise<any> {
    const token = this.getAuthToken();
    const response = await fetch(`${API_BASE_URL}/api/attachments/${attachmentId}`, {
      method: 'PUT',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` })
      },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update attachment');
    }

    return response.json();
  }

  async deleteAttachment(attachmentId: number): Promise<void> {
    return this.request<void>(`/api/attachments/${attachmentId}`, {
      method: 'DELETE',
    });
  }

  async reorderAttachments(taskId: number, orders: Array<{ id: number, order: number }>): Promise<void> {
    return this.request<void>(`/api/tasks/${taskId}/attachments/reorder`, {
      method: 'PUT',
      body: JSON.stringify(orders),
    });
  }

  getAttachmentUrl(filePath: string): string {
    // Extract attachment ID from file path if it's a full path
    // Or use the file path directly if it's already a relative path
    if (filePath.includes('/attachments/')) {
      // Extract filename from path
      const filename = filePath.split('/attachments/')[1];
      // If we have attachment ID, use download endpoint
      // Otherwise, construct URL from file path
      return `${API_BASE_URL}/${filePath}`;
    }
    return `${API_BASE_URL}/${filePath}`;
  }

  getAttachmentImageUrl(attachmentId: number): string {
    return `${API_BASE_URL}/api/attachments/${attachmentId}/download`;
  }

  // AI Analysis - supports multiple files per document group
  async analyzeWithAI(pdfFiles1: File[], pdfFiles2?: File[] | null, taskId?: number, agentType?: string, language?: string): Promise<any> {
    const token = this.getAuthToken();
    const formData = new FormData();

    // Append all files from group 1
    pdfFiles1.forEach(file => {
      formData.append('pdf_files_1', file);
    });

    // Append all files from group 2 (if any)
    if (pdfFiles2 && pdfFiles2.length > 0) {
      pdfFiles2.forEach(file => {
        formData.append('pdf_files_2', file);
      });
    }

    if (taskId) {
      formData.append('task_id', taskId.toString());
    }
    if (agentType) {
      formData.append('agent_type', agentType);
    }
    if (language) {
      formData.append('language', language);
    }

    const response = await fetch(`${API_BASE_URL}/api/ai/analyze`, {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` })
      },
      body: formData
    });

    if (!response.ok) {
      let errorMessage = response.statusText;
      try {
        const error = await response.json();
        errorMessage = error.detail || error.message || errorMessage;
      } catch { }
      throw new Error(errorMessage || 'Failed to analyze documents');
    }

    return response.json();
  }

  // Backward compatible method (single files)
  async compareWithAI(pdfFile1: File, pdfFile2: File, taskId?: number, agentType?: string): Promise<any> {
    return this.analyzeWithAI([pdfFile1], [pdfFile2], taskId, agentType);
  }


  // Task AI Compare CRUD
  async getAIAgents(): Promise<any[]> {
    return this.request<any[]>(`/api/task-ai-compare/agents`);
  }

  async getTaskAIComparison(taskId: number): Promise<any> {
    return this.request<any>(`/api/task-ai-compare/task/${taskId}`);
  }

  async getTaskAIComparisonByAgent(taskId: number, agentType: string): Promise<any> {
    return this.request<any>(`/api/task-ai-compare/task/${taskId}/agent/${agentType}`);
  }

  async getAIAnalysisStatus(comparisonId: number): Promise<any> {
    return this.request<any>(`/api/task-ai-compare/${comparisonId}`);
  }

  async getAllTaskAIComparisons(taskId: number): Promise<any[]> {
    return this.request<any[]>(`/api/task-ai-compare/task/${taskId}/all`);
  }

  async deleteTaskAIComparison(comparisonId: number): Promise<void> {
    return this.request<void>(`/api/task-ai-compare/${comparisonId}`, {
      method: 'DELETE',
    });
  }
}

export const apiService = new ApiService();

